import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { PrescriptionDetails } from "../../interfaces/Visit";
import { User } from "../../interfaces/User";
import whatsAppIcon from "../../assets/images/icons/whatsapp.svg";
import { sendPrescription, getPrescriptionWhatsAppStatus } from "../../apis/visit";
import { getSettings } from "../../apis/setting";

type PrescriptionProps = {
  preview?: boolean;
  prescriptionDetails: PrescriptionDetails;
};

const LIQUID_FORMS = new Set(["syrup", "drops", "suspension", "solution", "injection", "linctus"]);

const VITAL_KEYS = ["bp", "pulse", "weight", "temp"] as const;
const VITAL_LABELS: Record<string, string> = { bp: "BP", pulse: "PULSE", weight: "WEIGHT", temp: "TEMP" };

function formatDose(val: any): string {
  const n = Number(val);
  if (!n || isNaN(n)) return "—";
  if (n === 0.25) return "¼";
  if (n === 0.5) return "½";
  if (n === 0.75) return "¾";
  if (n === 1.5) return "1½";
  if (n === 2.5) return "2½";
  return String(n);
}

function formatMeal(meal: string): string {
  return (meal || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const DoseBadge: React.FC<{ val: any; isLiquid?: boolean }> = ({ val, isLiquid }) => {
  const n = Number(val);
  const empty = !n || isNaN(n);
  const label = isLiquid && !empty ? `${n}ml` : formatDose(val);
  const style: React.CSSProperties = {
    display: "inline-block",
    width: 28,
    height: 28,
    lineHeight: "28px",
    textAlign: "center",
    borderRadius: "50%",
    fontSize: 12,
    fontWeight: 600,
    flexShrink: 0,
    verticalAlign: "middle",
    ...(empty
      ? { border: "1px solid #e5e7eb", color: "#d1d5db" }
      : { backgroundColor: "#4f46e5", color: "#fff" }),
  };
  return <span style={style}>{empty ? "—" : label}</span>;
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-bold tracking-widest text-indigo-600 uppercase mb-1">{children}</p>
);

const Prescription: React.FC<PrescriptionProps> = ({ preview, prescriptionDetails }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [instructions, setInstructions] = useState<string[]>([]);
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [waStatus, setWaStatus] = useState<{ status: string; sentAt: string; errorData?: any } | null>(null);
  const [clinicConfig, setClinicConfig] = useState<Record<string, string>>({});

  const user: User | null = JSON.parse(window.localStorage.getItem("user") || "{}");

  useEffect(() => {
    if (prescriptionDetails.advice) {
      try {
        const parsed = JSON.parse(prescriptionDetails.advice);
        setInstructions(parsed.blocks?.map((b: any) => b.text).filter(Boolean) ?? []);
      } catch {
        setInstructions([]);
      }
    }
  }, [prescriptionDetails.advice]);

  useEffect(() => {
    fetchWaStatus();
  }, [prescriptionDetails.visitId]);

  const fetchWaStatus = () => {
    getPrescriptionWhatsAppStatus(prescriptionDetails.visitId)
      .then((res: any) => {
        const d = res?.data?.data;
        if (d) setWaStatus({ status: d.status, sentAt: d.sentAt, errorData: d.errorData });
      })
      .catch(() => {});
  };

  useEffect(() => {
    getSettings()
      .then((res: any) => {
        const settings: any[] = res?.data?.data || [];

        // Clinic identity from configuration (updated by superadmin / settings page)
        const configMap: Record<string, string> = {};
        ["organization_name", "address", "phone"].forEach((code) => {
          const s = settings.find((s) => s.code === code);
          if (s?.value) configMap[code] = s.value;
        });
        setClinicConfig(configMap);

        // Custom header image
        const explicit = settings.find((s) => s.code === "custom_header_image");
        let imgVal = explicit?.value;
        if (!imgVal) {
          const candidate = settings.find(
            (s) =>
              typeof s.value === "string" &&
              (s.value.includes("visit-files/") || s.value.startsWith("http")) &&
              s.code.toLowerCase().includes("header")
          );
          imgVal = candidate?.value;
        }
        if (typeof imgVal === "string" && imgVal) setHeaderImageUrl(toAbsoluteUrl(imgVal));
      })
      .catch(() => {});
  }, []);

  const toAbsoluteUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("visit-files/"))
      return `https://amsa-projects-acl.s3.ap-south-1.amazonaws.com/${url}`;
    return `/${url.replace(/^\//, "")}`;
  };

  const handlePrint = () => {
    const content = divRef.current?.innerHTML;
    if (!content) return;
    const win = window.open("", "_blank", "width=900,height=1100");
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Prescription</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #111; background: #fff; padding: 24px; }
  table { border-collapse: collapse; }
  @page { size: A4; margin: 1cm; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>${content}
<script>
  window.onload = function() {
    setTimeout(function() {
      window.print();
      window.onafterprint = function() { window.close(); };
    }, 250);
  };
<\/script>
</body>
</html>`);
    win.document.close();
  };

  const handleSendWhatsApp = async () => {
    setSendingWhatsApp(true);
    try {
      await sendPrescription({}, prescriptionDetails.visitId);
      message.success("Prescription sent on WhatsApp");
      setTimeout(fetchWaStatus, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Failed to send prescription on WhatsApp";
      message.error(msg);
      setTimeout(fetchWaStatus, 500);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Filter vitals to the 4 supported keys that have a non-empty value
  const vitalsToShow = (prescriptionDetails.vitals ?? []).filter(
    (v) => VITAL_KEYS.includes(v.label as any) && v.value
  );

  if (!user) {
    return <div className="flex justify-center pt-20 text-gray-400">Loading...</div>;
  }

  return (
    <>
      {/* A4 prescription body */}
      <div
        ref={divRef}
        className="prescription-print-area mx-auto bg-white"
        style={{ maxWidth: 794, fontFamily: "Arial, sans-serif", fontSize: 13 }}
      >
        {/* ── Header ── */}
        <div style={{ borderBottom: "2px solid #e5e7eb", padding: "16px 24px" }}>
          {headerImageUrl && (
            <img
              src={headerImageUrl}
              alt="Clinic header"
              style={{ width: "100%", maxHeight: 100, objectFit: "contain", marginBottom: 8 }}
            />
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {clinicConfig.organization_name || user?.activeClinic?.name}
              </div>
              <div style={{ color: "#6b7280", fontSize: 13, marginTop: 2 }}>
                {clinicConfig.address || user?.activeClinic?.address}
              </div>
              <div style={{ color: "#6b7280", fontSize: 13, marginTop: 2 }}>
                ☎ {clinicConfig.phone || user?.activeClinic?.phone}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ color: "#6b7280", fontSize: 13 }}>{user?.degree}</div>
              <div style={{ color: "#6b7280", fontSize: 13 }}>Reg. No. {user?.registrationNumber}</div>
            </div>
          </div>
        </div>

        {/* ── Patient row ── */}
        <div style={{ borderBottom: "1px solid #e5e7eb", padding: "10px 24px" }}>
          <div style={{ color: "#4f46e5", fontWeight: 700, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
            Patient
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {[
              { label: "NAME", value: `${prescriptionDetails.patient?.firstName ?? ""} ${prescriptionDetails.patient?.lastName ?? ""}`.trim() },
              { label: "GENDER", value: prescriptionDetails.patient?.gender },
              { label: "AGE", value: prescriptionDetails.patient?.age },
              { label: "PHONE", value: prescriptionDetails.patient?.phone },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={{ color: "#9ca3af", fontSize: 10, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 500, textTransform: "capitalize" }}>{value || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Vitals row (conditional) ── */}
        {vitalsToShow.length > 0 && (
          <div style={{ borderBottom: "1px solid #e5e7eb", padding: "10px 24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(vitalsToShow.length, 4)}, 1fr)`, gap: 8 }}>
              {vitalsToShow.map((v) => (
                <div key={v.label}>
                  <div style={{ color: "#4f46e5", fontSize: 10, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>
                    {VITAL_LABELS[v.label] ?? v.label.toUpperCase()}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {v.value} {v.unit}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Rx body ── */}
        <div style={{ padding: "16px 24px" }}>
          {/* Rx + Diagnosis */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <img src="/rx.png" alt="Rx" style={{ width: 34, flexShrink: 0 }} />
            {prescriptionDetails.finalDiagnosis ? (
              <span style={{ fontSize: 14 }}>
                <strong>Final Diagnosis:</strong> {prescriptionDetails.finalDiagnosis}
              </span>
            ) : prescriptionDetails.provisionalDiagnosis ? (
              <span style={{ fontSize: 14 }}>
                <strong>Provisional Diagnosis:</strong> {prescriptionDetails.provisionalDiagnosis}
              </span>
            ) : null}
          </div>

          {prescriptionDetails.symptoms?.length > 0 && (
            <div style={{ marginBottom: 12, fontSize: 13 }}>
              <strong>Symptoms:</strong> {prescriptionDetails.symptoms.join(", ")}
            </div>
          )}

          {/* Medicines table */}
          {prescriptionDetails.medicines?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: "#4f46e5", fontWeight: 700, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                Medicines
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ textAlign: "left", padding: "6px 8px 6px 0", fontWeight: 600, width: "36%" }}>Name</th>
                    {/* M / Noon / E / N sub-headers */}
                    <th style={{ textAlign: "center", padding: "6px 4px", fontWeight: 600, width: 40 }}>M</th>
                    <th style={{ textAlign: "center", padding: "6px 4px", fontWeight: 600, width: 40 }}>A</th>
                    <th style={{ textAlign: "center", padding: "6px 4px", fontWeight: 600, width: 40 }}>E</th>
                    <th style={{ textAlign: "center", padding: "6px 4px", fontWeight: 600, width: 40 }}>N</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>Meal</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>Duration</th>
                    <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600 }}>Notes</th>
                  </tr>
                  <tr>
                    <td />
                    <td colSpan={4} style={{ textAlign: "center", fontSize: 9, color: "#9ca3af", fontStyle: "italic", paddingBottom: 4 }}>
                      Morning · Afternoon · Evening · Night
                    </td>
                    <td colSpan={3} />
                  </tr>
                </thead>
                <tbody>
                  {prescriptionDetails.medicines.map((med, i) => {
                    const isLiquid = LIQUID_FORMS.has((med.instructions.form || "").toLowerCase());
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 8px 8px 0", fontWeight: 500, textTransform: "capitalize" }}>
                          {i + 1}. {med.instructions.name}
                          {med.instructions.molecule && (
                            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 400 }}>
                              {med.instructions.molecule}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: "center", padding: "8px 4px" }}>
                          <DoseBadge val={med.instructions.morning} isLiquid={isLiquid} />
                        </td>
                        <td style={{ textAlign: "center", padding: "8px 4px" }}>
                          <DoseBadge val={med.instructions.afternoon} isLiquid={isLiquid} />
                        </td>
                        <td style={{ textAlign: "center", padding: "8px 4px" }}>
                          <DoseBadge val={med.instructions.evening} isLiquid={isLiquid} />
                        </td>
                        <td style={{ textAlign: "center", padding: "8px 4px" }}>
                          <DoseBadge val={med.instructions.night} isLiquid={isLiquid} />
                        </td>
                        <td style={{ padding: "8px", whiteSpace: "nowrap", color: "#374151" }}>
                          {med.instructions.meal ? formatMeal(med.instructions.meal) : "—"}
                        </td>
                        <td style={{ padding: "8px", whiteSpace: "nowrap", color: "#374151" }}>
                          {med.instructions.duration
                            ? `${med.instructions.duration} ${med.instructions.durationPeriod ?? ""}`
                            : "—"}
                        </td>
                        <td style={{ padding: "8px 0", color: "#6b7280", fontSize: 12 }}>
                          {med.instructions.note || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Advice */}
          {instructions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: "#4f46e5", fontWeight: 700, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                Advice
              </div>
              <ol style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
                {instructions.map((line, i) => (
                  <li key={i} style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 13, color: "#374151" }}>
                    <span style={{ color: "#4f46e5", minWidth: 18, fontWeight: 500 }}>{i + 1}.</span>
                    {line}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        {/* ── Signature + Footer ── */}
        <div style={{ marginTop: 32, borderTop: "1px solid #e5e7eb" }}>
          {/* Doctor signature block — right aligned */}
          <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 24px 0" }}>
            <div style={{ textAlign: "center", minWidth: 180 }}>
              <div style={{ borderTop: "1px solid #374151", paddingTop: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div style={{ color: "#6b7280", fontSize: 12 }}>
                  {user?.degree} · Reg. No. {user?.registrationNumber}
                </div>
              </div>
            </div>
          </div>
          {/* Footer */}
          <div style={{ textAlign: "center", padding: "12px 24px", borderTop: "1px solid #e5e7eb", marginTop: 12 }}>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>
              This is digitally generated, requires no signature
            </span>
          </div>
        </div>
      </div>

      {/* Action buttons + delivery status — hidden during print */}
      <div className="flex flex-col gap-2 pt-3 prescription-no-print">
        {/* WhatsApp delivery status bar */}
        {waStatus && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
            waStatus.status === "read"      ? "bg-blue-50 text-blue-700 border border-blue-200" :
            waStatus.status === "delivered" ? "bg-green-50 text-green-700 border border-green-200" :
            waStatus.status === "sent"      ? "bg-indigo-50 text-indigo-700 border border-indigo-200" :
            waStatus.status === "failed"    ? "bg-red-50 text-red-700 border border-red-200" :
            "bg-gray-50 text-gray-600 border border-gray-200"
          }`}>
            <img alt="whatsapp" src={whatsAppIcon} className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">
              WhatsApp:{" "}
              {waStatus.status === "queued"    && "Queued — waiting for delivery confirmation"}
              {waStatus.status === "sent"      && "Sent to WhatsApp"}
              {waStatus.status === "delivered" && "Delivered to patient's phone"}
              {waStatus.status === "read"      && "Read by patient"}
              {waStatus.status === "failed"    && (
                <>
                  Failed to deliver
                  {waStatus.errorData?.[0]?.message && (
                    <span className="font-normal ml-1 opacity-80">— {waStatus.errorData[0].message}</span>
                  )}
                </>
              )}
            </span>
            <button
              onClick={fetchWaStatus}
              className="ml-auto text-xs underline opacity-70 hover:opacity-100"
              title="Refresh status"
            >
              Refresh
            </button>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={handlePrint}
            className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary"
          >
            Print
          </button>
          <button
            onClick={handleSendWhatsApp}
            disabled={sendingWhatsApp}
            className="px-4 py-2 text-sm font-semibold text-gray-800 rounded-md border border-gray-300 flex items-center gap-2 disabled:opacity-60"
          >
            <span>
              {sendingWhatsApp ? "Sending..." :
               waStatus && waStatus.status !== "failed" ? "Resend on WhatsApp" : "Send on WhatsApp"}
            </span>
            <img alt="whatsapp" src={whatsAppIcon} className="w-5 h-5" />
          </button>
        </div>
      </div>
    </>
  );
};

export default Prescription;
